import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StayStyle from '../../assets/stylesheets/stay/StayPage.module.css';
import AppStyle from '../../assets/stylesheets/App.module.css';
import WinePizza from "../../assets/img/sample/wine-pizza.jpg";
import Forest from "../../assets/img/sample/Forest.jpg";
import Farm from "../../assets/img/sample/Farm.jpg";
import Road from "../../assets/img/sample/Road.jpg";

import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';

const HERO_IMAGE_URL = "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1800&q=80";

// Farm Stay main page (Ontario): Hero + Search, Categories, Featured Stays, Values, Story, Newsletter, FAQ
export default function FarmStayPage() {
  const [faqOpen, setFaqOpen] = useState(null);
  const [form, setForm] = useState({ location: '', checkIn: '', checkOut: '', guests: 2 });

  const featuredStayTo = '/category/6?page=1&category=farmstays';

  const categories = [
    { key: 'niagara_winery', label: 'Niagara Wineries' },
    { key: 'maple', label: 'Maple Farms' },
    { key: 'organic', label: 'Organic Farms' },
    { key: 'lakeside', label: 'Lakeside Lodges' },
    { key: 'forest', label: 'Forest Retreats' },
    { key: 'wellness', label: 'Wellness & Yoga' },
    { key: 'family', label: 'Family & Kids' },
  ];

  const featured = [
    {
      id: 1,
      title: 'Sustainable Niagara Escarpment Winery Stay',
      region: 'Niagara, Ontario',
      img: WinePizza,
      badges: ['Vineyard', 'Organic'],
      desc: 'Small-scale winery stay practicing regenerative viticulture and natural fermentation.'
    },
    {
      id: 2,
      title: 'Maple Sap & Forest Ecology Farm Lodge',
      region: 'Muskoka, Ontario',
      img: Forest,
      badges: ['Maple', 'Low Impact'],
      desc: 'Early morning sap collection and eco-friendly timber cabin experience.'
    },
    {
      id: 3,
      title: 'Lakeside Regenerative Grazing Dairy Farm',
      region: 'Kawartha Lakes, Ontario',
      img: Farm,
      badges: ['Grass-fed', 'Solar'],
      desc: 'Adaptive pasture management and solar-powered, energy independent dairy.'
    },
    {
      id: 4,
      title: 'Northern Forest Rewilding Retreat',
      region: 'Algonquin Region, Ontario',
      img: Road,
      badges: ['Biodiversity', 'Dark Sky'],
      desc: 'Native habitat restoration zone and dark-sky stargazing program.'
    }
  ];

  const values = [
    { icon: '🌾', title: 'Regenerative Soil Stewardship', text: 'Cover crops, rotational grazing and carbon sequestration monitoring.' },
    { icon: '💧', title: 'Watershed & Lake Protection', text: 'Greywater reuse and non-point source pollution mitigation.' },
    { icon: '⚡', title: 'Clean Distributed Energy', text: 'Solar / geothermal plus transparent energy usage reporting.' },
    { icon: '🧑‍🌾', title: 'Local Food Sovereignty', text: 'Ontario-local supply chains and direct market programs.' },
    { icon: '🌲', title: 'Biodiversity Restoration', text: 'Native species plantings and wildlife corridor conservation.' },
    { icon: '🤝', title: 'Community Collaboration', text: 'Visitor education and partnerships with local makers.' },
  ];

  const faqs = [
    { q: 'What is a Farm Stay?', a: 'Accommodation on a working farm, winery, or forest retreat in Ontario where guests experience nature and farm life through seasonal activities.' },
    { q: 'Is it suitable for families and children?', a: 'Most listings are family-friendly, though some have age limits or restricted areas for safety. Check each listing’s Family section and house rules.' },
    { q: 'What is the pet policy?', a: 'Only select hosts allow pets and may charge a fee or require prior notice. Look for "Pet-friendly" on the listing and follow on-site animal guidelines.' },
    { q: 'What safety guidelines should guests follow?', a: 'Active farms have inherent risks. Stay within designated areas, supervise children at all times, avoid equipment and livestock unless guided by the host, and follow posted instructions.' },
  ];

  function toggleFaq(i) { setFaqOpen(faqOpen === i ? null : i); }
  function handleChange(e){ const { name, value } = e.target; setForm(p=>({...p,[name]:value})); }
  function handleSubmit(e){ e.preventDefault(); alert('Search feature coming soon.'); }

  return (
    <div className={StayStyle["farmstay-page"]}>
      <main className={StayStyle["farmstay-main-content"]}>
        <section className={AppStyle["section"]}>
          <div className={AppStyle["container"]}>
            <div className={`${StayStyle.notice} ${StayStyle['notice--blue']}`} role="note">
              <span aria-hidden="true" className={StayStyle['notice-emoji']}>ℹ️</span>
              <p className={StayStyle['notice-text']}>
                Farm Stay is under active development. We’re building something exciting. The page you’re viewing is a mockup and is not yet functional.
              </p>
            </div>
          </div>
        </section>

        <section className={`${AppStyle["section"]} ${StayStyle["stay-hero-section"]}`} aria-label="Farm Stay">
          <div className={`${AppStyle["container"]} ${StayStyle["stay-hero-container"]}`}>
            <div className={StayStyle["stay-top"]}>
              <div
                className={StayStyle["stay-hero-media"]}
                style={{
                  backgroundImage: `url(${HERO_IMAGE_URL})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat'
                }}
              />
              <div className={StayStyle["stay-overlay"]} />
              <div className={StayStyle["stay-content"]}>
                <h1 className={StayStyle["stay-title"]}>Explore Ontario’s Countryside Through Authentic Farm Stays</h1>
                <p className={StayStyle["stay-sub"]}>Explore regenerative farms, wineries and forest retreats across Ontario and get ready for the upcoming pilot launch.</p>
                <div className={StayStyle["stay-search-card"]} role="search">
                  <form onSubmit={handleSubmit} className={StayStyle["stay-search-grid"]} aria-label="Farm stay search form">
                    <div className={StayStyle["stay-field"]}>
                      <label htmlFor="location">Location / Keyword</label>
                      <input name="location" id="location" placeholder="e.g. Niagara, winery" value={form.location} onChange={handleChange} />
                    </div>
                    <div className={StayStyle["stay-field"]}>
                      <label htmlFor="checkIn">Check-in</label>
                      <input type="date" name="checkIn" id="checkIn" value={form.checkIn} onChange={handleChange} />
                    </div>
                    <div className={StayStyle["stay-field"]}>
                      <label htmlFor="checkOut">Check-out</label>
                      <input type="date" name="checkOut" id="checkOut" value={form.checkOut} onChange={handleChange} />
                    </div>
                    <div className={StayStyle["stay-field"]}>
                      <label htmlFor="guests">Guests</label>
                      <select name="guests" id="guests" value={form.guests} onChange={handleChange}>
                        {[1,2,3,4,5,6,7,8].map(n=> <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
 
                    {/* <button type="submit" className={StayStyle["stay-search-btn"]}>
                      <SearchIcon aria-hidden="true" />
                      <span>Search</span>
                    </button> */}
                  </form>

                  <button type="submit" className={StayStyle["stay-search-btn"]}>
                    <SearchIcon aria-hidden="true" />
                    <span>Search</span>
                  </button>

                </div>
                {/* <div className={StayStyle["stay-categories"]} aria-label="Browse categories">
                  <div className={StayStyle["stay-cat-scroll"]}>
                    {categories.map(c => <button key={c.key} className={StayStyle["stay-cat-pill"]} type="button">{c.label}</button>)}
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Stays */}
        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]} ${StayStyle["featured-section"]}`} aria-labelledby="featured-heading">
          <div className={AppStyle["container"]}>
            <h2 id="featured-heading" className={StayStyle["section-heading"]}>Featured Ontario Farm Stays</h2>
            <div className={StayStyle["stay-featured-grid"]}>
              {featured.map(item => (
                <Link
                  key={item.id}
                  to={featuredStayTo}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  aria-label={`View ${item.title}`}
                >
                  <article className={StayStyle["stay-card"]} aria-label={item.title}>
                    <img src={item.img} alt={item.title} loading="lazy" />
                    <div className={StayStyle["stay-card-body"]}>
                      <span className={StayStyle["stay-region"]}>{item.region}</span>
                      <h3 className={StayStyle["stay-card-title"]}>{item.title}</h3>
                      <p className={StayStyle["stay-card-desc"]}>{item.desc}</p>
                      <div className={StayStyle["stay-badge-row"]}>
                        {item.badges.map(b => <span className={StayStyle["stay-badge"]} key={b}>{b}</span>)}
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]} ${StayStyle["values-section"]}`} aria-labelledby="values-heading">
          <div className={AppStyle["container"]}>
            <h2 id="values-heading" className={StayStyle["section-heading"]}>What Matters to Us</h2>
            <div className={StayStyle["stay-values-grid"]}>
              {values.map(v => (
                <div className={StayStyle["stay-value-box"]} key={v.title}>
                  <h3><span aria-hidden="true">{v.icon}</span>{v.title}</h3>
                  <p style={{margin:0, fontSize:14, lineHeight:1.5, color:'#475569'}}>{v.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Story */}
        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]} ${StayStyle["story-section"]}`} aria-labelledby="story-heading">
          <div className={AppStyle["container"]}>
            <div className={StayStyle["stay-story"]}>
              <div className={StayStyle["stay-story-img"]}>
                <img src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1000&q=60" alt="Ontario forest and lake landscape" />
              </div>
              <div className={StayStyle["story-text"]}>
                <h3 id="story-heading">Travel That Accelerates Agricultural & Ecological Recovery in Ontario</h3>
                <p>Ontario Farm Stay is not just a list of accommodations. We surface farms advancing regenerative soil management, water stewardship, clean energy transition and local food sovereignty—then make their progress transparent.</p>
                <p>From Niagara vineyards to maple forests, lakeside grazing dairies and northern rewilding projects—you will soon be able to see how staying contributes to ecological and local economic resilience through meaningful data during our 2025 pilot.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter */}
        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]} ${StayStyle["newsletter-section"]}`} aria-labelledby="news-heading">
          <div className={AppStyle["container"]}>
            <div className={StayStyle["stay-newsletter"]}>
              <div>
                <h3 id="news-heading">Get Ontario Pilot Updates First</h3>
                <p style={{margin:0, fontSize:15, lineHeight:1.5, color:'#334155'}}>Join the waitlist to receive the first beta invite and notifications about new farm onboarding.</p>
              </div>
              <form onSubmit={(e)=>{e.preventDefault(); alert('Subscription feature coming soon.');}} aria-label="newsletter subscribe">
                <input type="email" required placeholder="Enter your email" aria-label="Email" />
                <button type="submit">Subscribe</button>
              </form>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={`${AppStyle["section"]} ${AppStyle["card-section"]} ${StayStyle["faq-section"]}`} aria-labelledby="faq-heading">
          <div className={AppStyle["container"]}>
            <h2 id="faq-heading" className={StayStyle["section-heading"]}>Frequently Asked Questions</h2>
            <div className={StayStyle["stay-faq-list"]}>
              {faqs.map((f,i)=>(
                <div key={f.q} className={`${StayStyle["stay-faq-item"]} ${faqOpen===i?'open':''}`}>
                  <button className={StayStyle["stay-faq-head"]} aria-expanded={faqOpen===i} onClick={()=>toggleFaq(i)}>
                    <h4>{f.q}</h4>
                    <span className={StayStyle["stay-arrow"]} aria-hidden="true">{faqOpen===i ? '▼' : '▶'}</span>
                  </button>
                  {faqOpen===i && <div className={StayStyle["stay-faq-body"]}>{f.a}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
